import { useCallback, useEffect, useState } from "react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import {
  AlertTriangle,
  BarChart3,
  FileText,
  Pause,
  Pencil,
  Play,
  RefreshCw,
  RotateCcw,
  XCircle,
} from "lucide-react";
import { Link } from "react-router";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import type { CrmDataProvider } from "../providers/types";
import type {
  DashboardCapabilities,
  WhatsAppBroadcast,
  WhatsAppBroadcastRecipient,
} from "../types";
import { canEditBroadcast } from "./broadcastEditDraft";
import { WhatsAppBroadcastDialog } from "./WhatsAppBroadcastDialog";

export function WhatsAppBroadcastsPage() {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [broadcasts, setBroadcasts] = useState<WhatsAppBroadcast[]>([]);
  const [loading, setLoading] = useState(false);
  const [capabilities, setCapabilities] =
    useState<DashboardCapabilities | null>(null);
  const [capabilityError, setCapabilityError] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingBroadcast, setEditingBroadcast] =
    useState<WhatsAppBroadcast | null>(null);
  const [recipientBroadcast, setRecipientBroadcast] =
    useState<WhatsAppBroadcast | null>(null);
  const [recipients, setRecipients] = useState<WhatsAppBroadcastRecipient[]>(
    [],
  );
  const featureFlags = capabilities?.features ?? {};
  const broadcastsEnabled = featureFlags.whatsapp_broadcasts !== false;
  const workerEnabled = Boolean(featureFlags.whatsapp_broadcast_worker);
  const schedulingEnabled = featureFlags.scheduled_broadcasts !== false;
  const analyticsDisabled = featureFlags.whatsapp_broadcast_analytics === false;

  const loadCapabilities = useCallback(async () => {
    try {
      setCapabilities(await dataProvider.getDashboardCapabilities());
      setCapabilityError(null);
    } catch (error) {
      setCapabilityError(errorMessage(error));
    }
  }, [dataProvider]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBroadcasts(await dataProvider.listWhatsAppBroadcasts());
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [dataProvider, notify]);

  useEffect(() => {
    void loadCapabilities();
    void load();
  }, [load, loadCapabilities]);

  const transition = async (
    broadcast: WhatsAppBroadcast,
    action: "pause" | "resume" | "cancel" | "retry-failed",
  ) => {
    try {
      await dataProvider.transitionWhatsAppBroadcast(broadcast.id, action);
      await load();
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    }
  };

  const openRecipients = async (broadcast: WhatsAppBroadcast) => {
    setRecipientBroadcast(broadcast);
    try {
      setRecipients(
        await dataProvider.getWhatsAppBroadcastRecipients(broadcast.id),
      );
    } catch (error) {
      notify(errorMessage(error), { type: "error" });
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">
            {translate("crm.whatsapp.broadcasts.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {translate("crm.whatsapp.broadcasts.summary", {
              total: broadcasts.length,
            })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/whatsapp_templates">
              <FileText className="size-4" />
              {translate("crm.whatsapp.templates.title")}
            </Link>
          </Button>
          <Button
            variant="outline"
            onClick={() => void load()}
            disabled={loading}
          >
            <RefreshCw className="size-4" />
            {translate("ra.action.refresh")}
          </Button>
          <Button
            onClick={() => setComposerOpen(true)}
            disabled={!broadcastsEnabled}
          >
            {translate("crm.whatsapp.broadcasts.new")}
          </Button>
        </div>
      </div>

      {(capabilityError || !broadcastsEnabled) && (
        <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertTitle>
            {translate("crm.whatsapp.capabilities.broadcasts_unavailable")}
          </AlertTitle>
          <AlertDescription>
            {capabilityError ||
              translate("crm.whatsapp.capabilities.broadcasts_disabled")}
          </AlertDescription>
        </Alert>
      )}

      {broadcastsEnabled && capabilities && !workerEnabled && (
        <Alert>
          <AlertTriangle className="size-4" />
          <AlertTitle>
            {translate("crm.whatsapp.capabilities.worker_unavailable")}
          </AlertTitle>
          <AlertDescription>
            {translate("crm.whatsapp.capabilities.worker_disabled")}
          </AlertDescription>
        </Alert>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{translate("crm.whatsapp.broadcasts.name")}</TableHead>
              <TableHead>
                {translate("crm.whatsapp.broadcasts.template")}
              </TableHead>
              <TableHead>Sender</TableHead>
              <TableHead>
                {translate("crm.whatsapp.broadcasts.status")}
              </TableHead>
              <TableHead>
                {translate("crm.whatsapp.broadcasts.progress")}
              </TableHead>
              <TableHead>Conversions</TableHead>
              <TableHead>
                {translate("crm.whatsapp.broadcasts.created")}
              </TableHead>
              <TableHead className="text-right">
                {translate("crm.whatsapp.broadcasts.actions")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {broadcasts.map((broadcast) => (
              <TableRow key={String(broadcast.id)}>
                <TableCell className="font-medium">{broadcast.name}</TableCell>
                <TableCell>
                  <div>{broadcast.template_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {broadcast.template_language}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-mono text-xs">
                    {broadcast.sender_key || "legacy primary"}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={
                      broadcast.failed_count ? "destructive" : "secondary"
                    }
                  >
                    {broadcast.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    {broadcast.read_count ?? 0} read,{" "}
                    {broadcast.delivered_count ?? 0} delivered,{" "}
                    {broadcast.failed_count ?? 0} failed
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {broadcast.recipient_count} recipients
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    {broadcast.create_command_count ?? 0} /create,{" "}
                    {broadcast.echo_started_count ?? 0} Echo
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {broadcast.final_video_delivered_count ?? 0} final,{" "}
                    {broadcast.ignored_no_conversion_count ?? 0} no conversion
                  </div>
                </TableCell>
                <TableCell>
                  <div>{formatDate(broadcast.created_at)}</div>
                  {broadcast.scheduled_for && (
                    <div className="text-xs text-muted-foreground">
                      {translate(
                        "crm.whatsapp.broadcasts.scheduled_for_short",
                        {
                          date: formatDate(broadcast.scheduled_for),
                        },
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    {!analyticsDisabled && hasBroadcastAnalytics(broadcast) && (
                      <Button
                        size="sm"
                        variant="outline"
                        asChild
                        title={translate("crm.whatsapp.analytics.view")}
                      >
                        <Link
                          aria-label={translate("crm.whatsapp.analytics.view")}
                          className="whitespace-nowrap"
                          to={`/whatsapp_broadcasts/${encodeURIComponent(
                            String(broadcast.id),
                          )}/analytics`}
                        >
                          <BarChart3 className="size-4" />
                          {translate("crm.whatsapp.analytics.view")}
                        </Link>
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void openRecipients(broadcast)}
                    >
                      {translate("crm.whatsapp.broadcasts.recipients")}
                    </Button>
                    {canEditBroadcast(broadcast) && (
                      <Button
                        size="sm"
                        variant="outline"
                        title={translate("crm.whatsapp.broadcasts.edit")}
                        aria-label={translate("crm.whatsapp.broadcasts.edit")}
                        onClick={() => setEditingBroadcast(broadcast)}
                      >
                        <Pencil className="size-4" />
                        {translate("crm.whatsapp.broadcasts.edit_action")}
                      </Button>
                    )}
                    {broadcast.status === "sending" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void transition(broadcast, "pause")}
                      >
                        <Pause className="size-4" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void transition(broadcast, "resume")}
                      >
                        <Play className="size-4" />
                      </Button>
                    )}
                    {!!broadcast.failed_count && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          void transition(broadcast, "retry-failed")
                        }
                      >
                        <RotateCcw className="size-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void transition(broadcast, "cancel")}
                    >
                      <XCircle className="size-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!broadcasts.length && (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center">
                  {loading
                    ? translate("crm.common.loading")
                    : translate("crm.whatsapp.broadcasts.empty")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <WhatsAppBroadcastDialog
        open={composerOpen}
        onOpenChange={setComposerOpen}
        onCreated={() => void load()}
        schedulingEnabled={schedulingEnabled}
      />

      {/* A separate instance, keyed by broadcast: mounting fresh per broadcast
          means the composer's own reset-on-open path hydrates the right draft
          instead of inheriting the previous one's state. */}
      {editingBroadcast && (
        <WhatsAppBroadcastDialog
          key={String(editingBroadcast.id)}
          open
          editing={editingBroadcast}
          onOpenChange={(open) => {
            if (!open) setEditingBroadcast(null);
          }}
          onCreated={() => {
            setEditingBroadcast(null);
            void load();
          }}
          schedulingEnabled
        />
      )}

      <RecipientsDialog
        broadcast={recipientBroadcast}
        recipients={recipients}
        onOpenChange={(open) => {
          if (!open) {
            setRecipientBroadcast(null);
            setRecipients([]);
          }
        }}
      />
    </div>
  );
}

function RecipientsDialog({
  broadcast,
  recipients,
  onOpenChange,
}: {
  broadcast: WhatsAppBroadcast | null;
  recipients: WhatsAppBroadcastRecipient[];
  onOpenChange: (open: boolean) => void;
}) {
  const translate = useTranslate();
  return (
    <Dialog open={!!broadcast} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{broadcast?.name}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[28rem] overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {translate("crm.whatsapp.broadcasts.contact")}
                </TableHead>
                <TableHead>
                  {translate("crm.whatsapp.broadcasts.status")}
                </TableHead>
                <TableHead>
                  {translate("crm.whatsapp.broadcasts.error")}
                </TableHead>
                <TableHead>
                  {translate("crm.whatsapp.broadcasts.updated")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recipients.map((recipient) => (
                <TableRow key={String(recipient.id)}>
                  <TableCell>
                    <div>{recipient.contact_name || recipient.contact_id}</div>
                    <div className="text-xs text-muted-foreground">
                      {recipient.whatsapp_user_id}
                    </div>
                  </TableCell>
                  <TableCell>{recipientDeliveryState(recipient)}</TableCell>
                  <TableCell className="max-w-xs truncate">
                    {recipient.provider_error_details ||
                      recipient.skip_reasons?.join(", ") ||
                      "-"}
                  </TableCell>
                  <TableCell>
                    {formatDate(recipient.status_updated_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function hasBroadcastAnalytics(broadcast: WhatsAppBroadcast) {
  const analyticsStatuses = new Set([
    "completed",
    "sent",
    "delivered",
    "read",
    "failed",
    "cancelled",
  ]);

  return (
    analyticsStatuses.has(broadcast.status) ||
    Boolean(broadcast.completed_at) ||
    (broadcast.accepted_count ?? 0) > 0 ||
    (broadcast.sent_count ?? 0) > 0 ||
    (broadcast.delivered_count ?? 0) > 0 ||
    (broadcast.read_count ?? 0) > 0 ||
    (broadcast.failed_count ?? 0) > 0 ||
    (broadcast.cancelled_count ?? 0) > 0
  );
}

function recipientDeliveryState(recipient: WhatsAppBroadcastRecipient) {
  if (
    recipientEffectiveReadAt(recipient) ||
    recipient.provider_status === "read"
  )
    return "read";
  if (recipient.first_delivered_at || recipient.provider_status === "delivered")
    return "delivered";
  if (recipient.first_sent_at || recipient.provider_status === "sent")
    return "sent";
  return recipient.provider_status || recipient.status || "accepted";
}

function recipientEffectiveReadAt(recipient: WhatsAppBroadcastRecipient) {
  return (
    recipient.first_read_at ||
    recipient.first_inbound_after_broadcast_at ||
    recipient.first_create_command_at ||
    recipient.first_echo_started_at ||
    recipient.first_generation_requested_at ||
    recipient.first_final_video_created_at ||
    recipient.first_final_video_delivered_at ||
    (recipientHasCreateCommand(recipient)
      ? recipient.first_inbound_after_broadcast_at ||
        recipient.status_updated_at ||
        recipient.first_delivered_at ||
        recipient.sent_at ||
        recipient.accepted_at
      : null) ||
    (recipient.provider_status === "read" ? recipient.status_updated_at : null)
  );
}

function recipientHasCreateCommand(recipient: WhatsAppBroadcastRecipient) {
  if (recipient.first_create_command_at) return true;
  const text = recipient.reply_text?.trim().toLowerCase().replace(/\s+/g, " ");
  if (!text) return false;
  return (
    text.split(" ")[0] === "/create" ||
    [
      "create",
      "create movie",
      "create my movie",
      "create new echo",
      "start my movie",
      "start new echo",
    ].includes(text)
  );
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "crm.whatsapp.error";
}
